import { useMemo } from 'react';
import {
  Progress,
  ResponseErrorPanel,
  DependencyGraph,
  DependencyGraphTypes,
  Link,
} from '@backstage/core-components';
import { useApi, configApiRef, useRouteRef } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  getEntityRelations,
  entityRouteRef,
  EntityDisplayName,
} from '@backstage/plugin-catalog-react';
import {
  RELATION_CHILD_OF,
  stringifyEntityRef,
  parseEntityRef,
} from '@backstage/catalog-model';
import useAsync from 'react-use/esm/useAsync';

// Tamanho fixo e definido aqui de propósito (não calculado a partir da
// tela): dá nós maiores que o padrão do Backstage (180x60), ocupando mais
// da área visível, com um teto que eu escolhi - nunca cresce além disso,
// diferente do fit="contain" (que reescala sem limite pra preencher
// qualquer container).
const NODE_WIDTH = 260;
const NODE_HEIGHT = 90;
const FONT_SIZE = 20;

type DiagramNode = { id: string; kind: string; name: string };

// Réplica do RenderNode original do GroupsDiagram (@backstage-community/
// plugin-explore), só trocando o visual pra usar os tokens de tema do
// portal em vez das classes CSS compiladas do pacote (que não dá pra
// reaproveitar fora do componente original).
function RenderNode(props: { node: DiagramNode }) {
  const catalogEntityRoute = useRouteRef(entityRouteRef);
  if (props.node.id === 'root') {
    return (
      <g>
        <rect width={NODE_WIDTH} height={NODE_HEIGHT} rx={8} fill="#334155" />
        <title>{props.node.name}</title>
        <foreignObject width={NODE_WIDTH} height={NODE_HEIGHT}>
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 8,
              boxSizing: 'border-box',
            }}
          >
            <div style={{ fontWeight: 'bold', fontSize: FONT_SIZE, color: '#fff', textAlign: 'center' }}>
              {props.node.name}
            </div>
          </div>
        </foreignObject>
      </g>
    );
  }
  const ref = parseEntityRef(props.node.id);
  return (
    <g>
      <rect width={NODE_WIDTH} height={NODE_HEIGHT} rx={8} fill="#0EA5E9" />
      <title>
        <EntityDisplayName entityRef={props.node.id} hideIcon disableTooltip />
      </title>
      <Link
        to={catalogEntityRoute({ kind: ref.kind, namespace: ref.namespace, name: ref.name })}
      >
        <foreignObject width={NODE_WIDTH} height={NODE_HEIGHT}>
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 8,
              boxSizing: 'border-box',
            }}
          >
            <div style={{ fontWeight: 'bold', fontSize: FONT_SIZE, color: '#fff', textAlign: 'center' }}>
              <EntityDisplayName entityRef={props.node.id} hideIcon />
            </div>
          </div>
        </foreignObject>
      </Link>
    </g>
  );
}

// Substitui o GroupsDiagram do @backstage-community/plugin-explore (1.0.1):
// aquele usa fit="contain", que SEMPRE escala o desenho pra preencher a
// caixa inteira, não importa o tamanho dela - com poucos grupos (conteúdo
// pequeno) numa caixa grande, isso amplia o desenho muito além do legível
// (não é questão de calcular a altura certa da caixa, é a própria lógica de
// escala do "contain"). Aqui é fit="grow": desenha os nós no tamanho natural
// (NODE_WIDTH x NODE_HEIGHT) e a caixa abraça esse tamanho, sem sobra de
// espaço vazio nem estourar a tela - width:'100%' é só pra não passar da
// largura da página; a altura não é fixada em nada, cresce com o conteúdo.
// zoom="enabled" continua disponível (scroll/pinch) pra quem quiser ampliar.
export function GroupsFitDiagram() {
  const configApi = useApi(configApiRef);
  const catalogApi = useApi(catalogApiRef);
  const organizationName = configApi.getOptionalString('organization.name') ?? 'Backstage';

  const {
    loading,
    error,
    value: catalogResponse,
  } = useAsync(() => catalogApi.getEntities({ filter: { kind: ['Group'] } }), [catalogApi]);

  const { nodes, edges } = useMemo(() => {
    const diagramNodes: DiagramNode[] = [{ id: 'root', kind: 'Organization', name: organizationName }];
    const diagramEdges: { from: string; to: string; label: string }[] = [];
    for (const item of catalogResponse?.items ?? []) {
      const id = stringifyEntityRef(item);
      const childOf = getEntityRelations(item, RELATION_CHILD_OF);
      diagramNodes.push({ id, kind: item.kind, name: '' });
      if (childOf.length === 0) {
        diagramEdges.push({ from: id, to: 'root', label: '' });
      }
      childOf.forEach(relation =>
        diagramEdges.push({ from: id, to: stringifyEntityRef(relation), label: '' }),
      );
    }
    return { nodes: diagramNodes, edges: diagramEdges };
  }, [catalogResponse, organizationName]);

  if (loading) {
    return <Progress />;
  }
  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  return (
    <div>
      <div style={{ width: '100%' }}>
        <DependencyGraph
          nodes={nodes}
          edges={edges}
          nodeMargin={32}
          rankMargin={90}
          direction={DependencyGraphTypes.Direction.RIGHT_LEFT}
          renderNode={RenderNode}
          zoom="enabled"
          fit="grow"
        />
      </div>
      <div style={{ fontSize: 12, color: 'var(--bui-fg-secondary, #757575)', marginTop: 4 }}>
        Use pinch &amp; zoom (ou a roda do mouse) para ajustar o zoom.
      </div>
    </div>
  );
}
